import { useState } from "react";
import type { GroupBalancesResponse, TransferResponse } from "@qota/shared";
import { Amount, Avatar, Button, Icon } from "@qota/ui";

/**
 * La tessera dei conti, in cima al gruppo.
 *
 * Una riga per verso — quello che devi, quello che ti devono — con la cifra
 * dentro la frase e la pila di chi è coinvolto. Toccando una riga si apre lì
 * sotto il dettaglio persona per persona, senza cambiare schermata.
 *
 * ── Perché non c'è il netto ────────────────────────────────────────────────
 *
 * Se devi 210 e ti devono 40, il netto è 170 — ed è un numero che **nessuno
 * pagherà mai a nessuno**. Le due righe mostrano invece due cifre che
 * corrispondono a bonifici veri. È la stessa ragione per cui non c'è un totale
 * per persona nella lista amici.
 *
 * ── Perché gli avatar non hanno un anello colorato ─────────────────────────
 *
 * Nelle bozze le facce stavano in una fila sola e serviva distinguere i due
 * versi. Con una riga per verso lo dice già la riga: l'anello sarebbe un terzo
 * colore sopra un avatar già colorato, e un segnale in più da imparare per chi
 * verde e corallo non li distingue.
 */
export interface BalanceTileProps {
  balances: GroupBalancesResponse | undefined;
  /** Il membro corrispondente a chi guarda. `null` finché il gruppo non è caricato. */
  myMemberId: string | null;
  /** Apre il pareggio, eventualmente già puntato su una persona. */
  onSettle: (withMemberId?: string) => void;
}

/** Oltre questo numero la pila si chiude con un contatore. */
const FACCE_VISIBILI = 4;

export function BalanceTile({ balances, myMemberId, onSettle }: BalanceTileProps) {
  // Due stati separati e non uno solo: i versi si aprono per conto loro.
  // Aprire i crediti chiudendo i debiti sarebbe un movimento che nessuno ha
  // chiesto, proprio mentre si sta leggendo l'altra metà.
  const [apertoDebiti, setApertoDebiti] = useState(false);
  const [apertoCrediti, setApertoCrediti] = useState(false);

  if (!balances || !myMemberId) return <TileSkeleton />;

  const debiti = balances.transfers.filter((transfer) => transfer.fromMemberId === myMemberId);
  const crediti = balances.transfers.filter((transfer) => transfer.toMemberId === myMemberId);

  if (debiti.length === 0 && crediti.length === 0) {
    return <InPari names={balances.balances.map((entry) => entry.displayName)} />;
  }

  return (
    <div style={tileStyle}>
      {debiti.length > 0 && (
        <Verso
          verso="debiti"
          transfers={debiti}
          nameOf={(transfer) => transfer.toDisplayName}
          aperto={apertoDebiti}
          onToggle={() => setApertoDebiti((was) => !was)}
          onAction={(transfer) => onSettle(transfer.toMemberId)}
          separa={false}
        />
      )}

      {crediti.length > 0 && (
        <Verso
          verso="crediti"
          transfers={crediti}
          nameOf={(transfer) => transfer.fromDisplayName}
          aperto={apertoCrediti}
          onToggle={() => setApertoCrediti((was) => !was)}
          onAction={(transfer) => onSettle(transfer.fromMemberId)}
          separa={debiti.length > 0}
        />
      )}
    </div>
  );
}

const tileStyle = {
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-card)",
  padding: "4px 14px",
} as const;

/**
 * Una direzione: la frase, le facce, e il dettaglio quando è aperta.
 *
 * Il comando che apre è **tutta la riga**, non il segno «⌄»: quel segno è alto
 * 14 pixel e serve solo a dire che si apre — un bersaglio da dito non ci sta.
 */
function Verso({
  verso,
  transfers,
  nameOf,
  aperto,
  onToggle,
  onAction,
  separa,
}: {
  verso: "debiti" | "crediti";
  transfers: TransferResponse[];
  nameOf: (transfer: TransferResponse) => string;
  aperto: boolean;
  onToggle: () => void;
  onAction: (transfer: TransferResponse) => void;
  separa: boolean;
}) {
  const totale = transfers.reduce((sum, transfer) => sum + transfer.amountCents, 0);
  const debito = verso === "debiti";

  // Con una controparte sola la frase fa il nome: è l'informazione che rende la
  // cifra azionabile, e con più di uno non ci starebbe.
  const solo = transfers.length === 1 ? nameOf(transfers[0]!) : null;

  return (
    <div style={separa ? { borderTop: "1px solid var(--border-subtle)" } : undefined}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={aperto}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "12px 0",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: "var(--font-text)",
          textAlign: "left",
        }}
      >
        <Pila names={transfers.map(nameOf)} />

        <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 500 }}>
          {debito ? "Devi " : "Ti devono "}
          <Amount cents={totale} size={18} tone={debito ? "negative" : "positive"} />
          {solo && (
            <>
              {debito ? " a " : " "}
              <strong>{solo}</strong>
            </>
          )}
        </span>

        <span
          style={{
            color: "var(--text-tertiary)",
            display: "flex",
            flexShrink: 0,
            transform: aperto ? "rotate(180deg)" : undefined,
          }}
        >
          <Icon name="chevron-down" size={16} />
        </span>
      </button>

      {aperto && (
        <div
          style={{
            background: "var(--surface-card)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-sm)",
            padding: "0 10px",
            marginBottom: 12,
          }}
        >
          {transfers.map((transfer, index) => (
            <div
              key={`${transfer.fromMemberId}-${transfer.toMemberId}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "9px 0",
                borderTop: index === 0 ? "none" : "1px solid var(--border-subtle)",
              }}
            >
              <Avatar name={nameOf(transfer)} size={26} />

              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, fontWeight: 600 }}>
                  {nameOf(transfer)}
                </span>
                {/* Il verso è scritto, non solo colorato: il colore da solo
                    non basta a chi non lo distingue. */}
                <span style={{ display: "block", fontSize: 10.5, color: "var(--text-secondary)" }}>
                  {debito ? "gli devi" : "ti deve"}
                </span>
              </span>

              <Amount
                cents={transfer.amountCents}
                size={13}
                tone={debito ? "negative" : "positive"}
              />

              <Button
                variant={debito ? "accent" : "secondary"}
                size="sm"
                onClick={() => onAction(transfer)}
              >
                {/* Dalla parte del credito quel pagamento non lo fai tu:
                    chiamarlo «Salda» sarebbe una promessa sbagliata. */}
                {debito ? "Salda" : "Sollecita"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Le facce, sovrapposte, con il contatore quando sono troppe. */
function Pila({ names }: { names: string[] }) {
  const visibili = names.slice(0, FACCE_VISIBILI);
  const restanti = names.length - visibili.length;

  return (
    <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      {visibili.map((name, index) => (
        <span key={`${name}-${index}`} style={index === 0 ? undefined : { marginLeft: -9 }}>
          <Avatar name={name} size={26} ring />
        </span>
      ))}

      {restanti > 0 && (
        <span
          style={{
            marginLeft: -9,
            width: 26,
            height: 26,
            borderRadius: "var(--radius-pill)",
            background: "var(--border-strong)",
            color: "var(--text-secondary)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-display)",
            fontSize: 9,
            fontWeight: 700,
            boxShadow: "0 0 0 2px var(--surface-sunken)",
          }}
        >
          +{restanti}
        </span>
      )}
    </span>
  );
}

/** Nessun conto aperto: una frase e le facce, nessun comando da premere. */
function InPari({ names }: { names: string[] }) {
  return (
    <div style={{ ...tileStyle, padding: "14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Pila names={names} />
        <span style={{ flex: 1, fontSize: 15, fontWeight: 500 }}>Siete in pari</span>
        <span style={{ color: "var(--money-positive)", display: "flex", flexShrink: 0 }}>
          <Icon name="circle-check" size={18} />
        </span>
      </div>
    </div>
  );
}

function TileSkeleton() {
  return <div style={{ ...tileStyle, height: 64 }} aria-hidden="true" />;
}
